import { createContext, type PropsWithChildren, use, useCallback, useEffect, useRef } from "react";

import { swSettings } from "app/common/services/swSettings";

export const MESSAGE_TYPE_DICT = {
    openChiliPiper: "openChiliPiper",
    switchPackage: "switchPackage",
    openGaConnect: "openGaConnect",
} as const;

export interface IMessageEvent<TPayload = unknown> extends MessageEvent {
    type: MessageTypeName;
    payload: TPayload;
}

type IMessageEventHandler = (event: MessageEvent<IMessageEvent>) => void;

export type MessageTypeName = keyof typeof MESSAGE_TYPE_DICT;

interface IMessagesContextValue {
    register:
        | (<TPayload = unknown>(
              type: MessageTypeName,
              handler: (event: MessageEvent<IMessageEvent<TPayload>>) => void,
          ) => () => void)
        | null;
}

const getAllowedSites = (): readonly string[] => Object.values(swSettings.swsites);

const MessagesContext = createContext<IMessagesContextValue>({ register: null });

export const MessagesContextProvider = ({ children }: PropsWithChildren) => {
    const handlers = useRef<Map<MessageTypeName, Set<IMessageEventHandler>>>(
        new Map<MessageTypeName, Set<IMessageEventHandler>>(),
    );

    useEffect(() => {
        const messageHandler = (event: MessageEvent<IMessageEvent>) => {
            if (getAllowedSites().includes(event.origin)) {
                handlers.current?.get(event.data?.type)?.forEach((handler) => handler(event));
            }
        };

        window.addEventListener("message", messageHandler);

        return () => {
            window.removeEventListener("message", messageHandler);
        };
    }, []);

    const register = useCallback(
        <TPayload = unknown,>(
            type: MessageTypeName,
            handler: (event: MessageEvent<IMessageEvent<TPayload>>) => void,
        ) => {
            if (!handlers.current.has(type)) {
                handlers.current.set(type, new Set());
            }

            handlers.current.get(type)?.add(handler as IMessageEventHandler);

            return () => {
                handlers.current.get(type)?.delete(handler as IMessageEventHandler);
            };
        },
        [],
    );

    return <MessagesContext.Provider value={{ register }}>{children}</MessagesContext.Provider>;
};

export const useMessagesContext = () => use(MessagesContext);
